import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "store/index";
import { useTranslation } from "react-i18next";

import {
  getRiskKycRulesList,
  handlePaginate,
} from "store/risk/kyc/rules/slice";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import KeyRulesFilter from "./components/KeyRulesFilter";
import KeyRulesHeader from "./components/KeyRulesFilterHeader";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import { riskKycRulesLimits } from "configs/limits";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import EmptyState from "components/EmptyState/EmptyState";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

const KycRulesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const list = useAppSelector((store) => store.riskKycRules.list);
  const isListFetched = useAppSelector(
    (store) => store.riskKycRules.isListFetched,
  );

  const [limit, setLimit] = useState<number>(riskKycRulesLimits[0]);
  const [filterByTitle, setFilterByTitle] = useState<string>("");
  const [filterByCode, setFilterByCode] = useState<string>("");
  const totalItems = useAppSelector((store) => store.riskKycRules.totalItems);
  const totalPages = useAppSelector((store) => store.riskKycRules.totalPages);
  const activePage = useAppSelector((store) => store.riskKycRules.activePage);
  const processingItemId = useAppSelector(
    (store) => store.riskKycRules.processingItemId,
  );
  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'riskKycRules',
    isListFetched
  });

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOpenCreateModal = () => {
    if (!permissions?.create) return;

    navigate(`${location.pathname}?activeModal=${ModalTypes.riskKycRules}`);
    dispatch(openModal({ activeModal: ModalTypes.riskKycRules }));
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.riskKycRules}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.riskKycRules }));
  };

  // const handleDeleteItem = (id: number) => {
  //   dispatch(deleteRiskKycPresetsItem(id))
  //     .unwrap()
  //     .then(() => {
  //       dispatch(
  //         showNotification({
  //           title: t("error"),
  //           message: t("entityDeleteSuccess"),
  //           type: NotificationTypes.success,
  //         }),
  //       );
  //     })
  //     .catch(() => {
  //       dispatch(
  //         showNotification({
  //           title: t("error"),
  //           message: t("entityDeleteError"),
  //           type: NotificationTypes.error,
  //         }),
  //       );
  //     });
  // }

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getRiskKycRulesList({
          limit,
          offset: activePage - 1,
        }),
      );
    }
  }, [isListFetched, activePage]);

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <KeyRulesFilter
        filterByTitle={filterByTitle}
        filterByCode={filterByCode}
        onFilterSearchByTitle={setFilterByTitle}
        onFilterSearchByCode={setFilterByCode}
        handleApply={() => { }}
        
      />
      <div className="page-list--with-filter-content">
        <KeyRulesHeader
          createModal={handleOpenCreateModal}
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
        />
        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={3} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && list.length ? (
          <>
            <div className="game-table-wrap">
              <Table
                data={list}
                idField="id"
                hasCheckbox={false}
                columns={{
                  sortingColumns: {
                    id: {
                      value: t("id"),
                      hasSorting: false,
                    },
                    title: {
                      value: t("title"),
                      hasSorting: false,
                    },
                    code: {
                      value: t("code"),
                      hasSorting: false,
                    },
                    isActive: {
                      value: t("active"),
                      hasSorting: false,
                    },
                  },
                  hiddenColumns: ["createdAt", "updatedAt"],
                }}
                processingId={processingItemId}
                // onDelete={handleDeleteItem}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            </div>
            <Pagination
              handleLimitClick={(e) => {
                setLimit(e);
                dispatch(handlePaginate(1));
              }}
              limitsArray={riskKycRulesLimits}
              activePage={activePage}
              limit={limit}
              totalItems={totalItems}
              totalPages={totalPages}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePreviousClick}
            />
          </>
        ) : null}

        {isListFetched && !list.length && <EmptyState />}
      </div>
    </div>
  );
};

export default KycRulesList;
